import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { ActiveTabContext } from './ActiveTabContext';

export interface ActiveTabContextProviderProps {}

function hasFocus() {
  return document.hasFocus();
}

const ActiveTabContextProvider: React.FC<ActiveTabContextProviderProps> = ({ children }) => {
  // Window focusing
  const [isWindowFocused, setIsWindowFocused] = useState<boolean>(hasFocus());

  // Update window focusing
  React.useEffect(() => {
    setIsWindowFocused(hasFocus()); // Focus for following renders.

    const onFocus = () => setIsWindowFocused(true);
    const onBlur = () => setIsWindowFocused(false);

    // Add event listeners
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    // Cleanup
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Team selection
  const getSelectedTeamId = () => localStorage.getItem('team-id');
  const [currentTeamId, setCurrentTeamId] = useState<string>(getSelectedTeamId());

  const handleSetCurrentTeamId = useCallback((id: number, skipReload?: boolean) => {
    localStorage.setItem('team-id', id.toString());
    setCurrentTeamId(id.toString());
    !skipReload && window.location.reload();
  }, []);

  // Automatically switch to error page if team has been changed upon reopening tab
  const history = useHistory();
  React.useEffect(() => {
    if (!isWindowFocused) return;
    if (!currentTeamId) return;
    if (!getSelectedTeamId()) return;

    if (currentTeamId !== getSelectedTeamId()) {
      history.push('/team-mismatch');
    }
  }, [isWindowFocused]);

  // Create value for context
  const value = useMemo(
    () => ({ isWindowFocused, currentTeamId, setCurrentTeamId: handleSetCurrentTeamId }),
    [isWindowFocused]
  );

  return <ActiveTabContext.Provider value={value}>{children}</ActiveTabContext.Provider>;
};

export default ActiveTabContextProvider;
